<template>
  <div class="pubyun-ddns-container">
    <!-- PubYun密钥配置卡片 -->
    <div class="config-card">
      <div class="card-header">
        <h3 class="card-title">{{ $t('pubyunDdns.configTitle') }}</h3>
        <el-button type="primary" size="small" @click="showKeyEditDialog">
          <el-icon><Edit /></el-icon>
          {{ $t('pubyunDdns.editConfig') }}
        </el-button>
      </div>
      <div class="card-body">
        <el-alert
          :title="$t('pubyunDdns.alertUpdateInterval')"
          type="info"
          :closable="false"
          class="alert-message"
        />
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">{{ $t('pubyunDdns.username') }}</span>
            <span class="info-value">{{ keyForm.keyID || $t('pubyunDdns.notConfigured') }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('pubyunDdns.password') }}</span>
            <span class="info-value">{{ keyForm.keySecret ? '••••••••' : $t('pubyunDdns.notConfigured') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- DDNS列表卡片 -->
    <div class="ddns-card">
      <div class="card-header">
        <div class="card-title-wrapper">
          <h3 class="card-title">{{ $t('pubyunDdns.ddnsList') }}</h3>
          <span class="status-tag" :class="dnsSwitch ? 'enabled' : 'disabled'">
            {{ dnsSwitch ? $t('pubyunDdns.serviceEnabled') : $t('pubyunDdns.serviceDisabled') }}
          </span>
        </div>
        <div class="card-actions">
          <el-button type="info" size="small" @click="showLogDialog">
            <el-icon><Document /></el-icon>
            {{ $t('pubyunDdns.viewLogs') }}
          </el-button>
          <el-button type="primary" size="small" @click="showDnsEditDialog(null)">
            <el-icon><Plus /></el-icon>
            {{ $t('pubyunDdns.addRecord') }}
          </el-button>
        </div>
      </div>

      <div class="card-body">
        <div v-if="dnsList.length === 0" class="empty-state">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('pubyunDdns.noRecords') }}</p>
        </div>

        <div v-else class="table-responsive">
          <table class="ddns-table">
            <thead>
              <tr>
                <th>{{ $t('pubyunDdns.name') }}</th>
                <th>{{ $t('pubyunDdns.domain') }}</th>
                <th>{{ $t('pubyunDdns.type') }}</th>
                <th>{{ $t('pubyunDdns.ipType') }}</th>
                <th>{{ $t('pubyunDdns.mappedIp') }}</th>
                <th>{{ $t('pubyunDdns.status') }}</th>
                <th>{{ $t('pubyunDdns.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in dnsList" :key="row.name">
                <td :data-label="$t('pubyunDdns.name')">{{ row.name }}</td>
                <td :data-label="$t('pubyunDdns.domain')">{{ row.domainName }}</td>
                <td :data-label="$t('pubyunDdns.type')">{{ row.type === 'A' ? $t('pubyunDdns.ipv4') : $t('pubyunDdns.ipv6') }}</td>
                <td :data-label="$t('pubyunDdns.ipType')">{{ row.flag ? $t('pubyunDdns.custom') : $t('pubyunDdns.autoGet') }}</td>
                <td :data-label="$t('pubyunDdns.mappedIp')">{{ row.value }}</td>
                <td :data-label="$t('pubyunDdns.status')">
                  <span class="status-badge" :class="getStatusClass(row.status)">
                    {{ formatStatus(row.status) }}
                  </span>
                </td>
                <td :data-label="$t('pubyunDdns.actions')" class="action-cell">
                  <div class="action-buttons">
                    <el-button
                      type="primary"
                      size="small"
                      @click="updateDns(row)"
                    >
                      {{ $t('pubyunDdns.update') }}
                    </el-button>
                    <el-button
                      type="danger"
                      size="small"
                      @click="deleteDns(row)"
                    >
                      {{ $t('pubyunDdns.delete') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- PubYun密钥编辑对话框 -->
    <el-dialog
      v-model="keyDialogVisible"
      :title="$t('pubyunDdns.editPubyunConfig')"
      :width="isMobile ? '95%' : '600px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        :model="keyForm"
        :rules="keyRules"
        ref="keyFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('pubyunDdns.username')" prop="keyID">
          <el-input v-model="keyForm.keyID" :placeholder="$t('pubyunDdns.inputUsername')" />
        </el-form-item>

        <el-form-item :label="$t('pubyunDdns.password')" prop="keySecret">
          <el-input
            v-model="keyForm.keySecret"
            type="password"
            show-password
            :placeholder="$t('pubyunDdns.inputPassword')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="keyDialogVisible = false">{{ $t('pubyunDdns.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="savePubyunKey"
            :loading="keyLoading"
          >
            {{ $t('pubyunDdns.saveConfig') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- DNS记录编辑对话框 -->
    <el-dialog
      v-model="dnsDialogVisible"
      :title="dnsDialogMode === 'edit' ? $t('pubyunDdns.editDnsRecord') : $t('pubyunDdns.addDnsRecord')"
      :width="isMobile ? '95%' : '600px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        :model="currentRecord"
        :rules="recordRules"
        ref="recordFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('pubyunDdns.recordName')" prop="name">
          <el-input v-model="currentRecord.name" :disabled="dnsDialogMode === 'edit'" :placeholder="$t('pubyunDdns.inputRecordName')" />
        </el-form-item>

        <el-form-item :label="$t('pubyunDdns.domain')" prop="domainName">
          <el-input v-model="currentRecord.domainName" :placeholder="$t('pubyunDdns.inputDomain')" />
        </el-form-item>

        <el-form-item :label="$t('pubyunDdns.recordType')" prop="type">
          <el-select v-model="currentRecord.type" style="width: 100%">
            <el-option :label="$t('pubyunDdns.ipv4Record')" value="A" />
            <el-option :label="$t('pubyunDdns.ipv6Record')" value="AAAA" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('pubyunDdns.ipType')">
          <el-select
            v-model="currentRecord.flagStr"
            style="width: 100%"
            disabled
          >
            <el-option :label="$t('pubyunDdns.autoGet')" value="auto" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('pubyunDdns.ipAddress')">
          <el-input
            v-model="currentRecord.value"
            readonly
            class="readonly-input"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dnsDialogVisible = false">{{ $t('pubyunDdns.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="saveDnsRecord"
            :loading="recordLoading"
          >
            {{ dnsDialogMode === 'edit' ? $t('pubyunDdns.update') : $t('pubyunDdns.save') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- PubYun DDNS日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('pubyunDdns.logTitle')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('pubyunDdns.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('pubyunDdns.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('pubyunDdns.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('pubyunDdns.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('pubyunDdns.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('pubyunDdns.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('pubyunDdns.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('pubyunDdns.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Edit, Plus, InfoFilled, Document, Refresh, Download } from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config"
import { useRouter } from 'vue-router'

const router = useRouter()
const isMobile = ref(false)

// 检测移动端
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

// 用户信息
const isAdmin = ref(false)
const isLogin = ref(false)
const loginUsername = ref('')

// PubYun密钥表单
const keyForm = ref({
  keyID: '',
  keySecret: ''
})

const keyFormRef = ref(null)
const keyLoading = ref(false)
const keyDialogVisible = ref(false)
const dnsDialogMode = ref('add')

// 密钥验证规则
const keyRules = {
  keyID: [
    { required: true, message: '请输入用户名', trigger: 'blur' }
  ],
  keySecret: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ]
}

// DDNS相关数据
const dnsList = ref([])
const dnsSwitch = ref(true)
const refreshInterval = ref(null)
const logRefreshInterval = ref(null)

// DNS记录编辑对话框相关
const dnsDialogVisible = ref(false)
const currentRecord = ref({
  name: '',
  domainName: '',
  type: 'A',
  flagStr: 'auto',
  value: '自动获取中...',
  flag: false
})
const recordFormRef = ref(null)
const recordLoading = ref(false)

// 日志对话框相关
const logDialogVisible = ref(false)
const logLoading = ref(false)
const logClearing = ref(false)
const logDownloading = ref(false)
const logsData = ref([])
const logTotal = ref(0)
const logCurrentPage = ref(1)
const logPageSize = ref(50)
const logContentRef = ref(null)
const autoRefreshEnabled = ref(true) // 默认开启自动刷新

// 获取状态样式类
const getStatusClass = (status) => {
  if (!status) return 'info'
  
  const lowerStatus = status.toLowerCase()
  
  if (lowerStatus.includes('成功') || lowerStatus.includes('正常')) {
    return 'success'
  } else if (lowerStatus.includes('失败') || lowerStatus.includes('错误')) {
    return 'danger'
  } else if (lowerStatus.includes('未激活') || lowerStatus.includes('关闭')) {
    return 'warning'
  } else {
    return 'info'
  }
}

// 记录验证规则
const recordRules = {
  name: [
    { required: true, message: '请输入记录名称', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9_-]{1,64}$/, message: '名称只能包含字母、数字、下划线和横线', trigger: 'blur' }
  ],
  domainName: [
    { required: true, message: '请输入域名', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9.-]{1,255}$/, message: '请输入有效的域名', trigger: 'blur' }
  ],
  type: [
    { required: true, message: '请选择记录类型', trigger: 'change' }
  ]
}

// 检查登录状态
const checkLogin = (data) => {
  if (data?.errCode === 1000) {
    ElMessage.warning('请先登录系统')
    router.push('/user-login?callback=pubyun-ddns')
    return false
  }
  return true
}

// 格式化状态显示
const formatStatus = (status) => {
  if (!status) return '未知状态'
  
  if (status.includes('成功')) return '成功'
  if (status.includes('失败')) return '失败'
  
  return status
}

// 显示密钥编辑对话框
const showKeyEditDialog = () => {
  keyDialogVisible.value = true
}

// 显示DNS记录编辑对话框
const showDnsEditDialog = (record) => {
  if (record) {
    // 编辑现有记录
    dnsDialogMode.value = 'edit'
    currentRecord.value = {
      ...record,
      flagStr: 'auto'
    }
  } else {
    // 新增记录
    dnsDialogMode.value = 'add'
    currentRecord.value = {
      name: '',
      domainName: '',
      type: 'A',
      flagStr: 'auto',
      value: '自动获取中...',
      flag: false,
      status: '未配置'
    }
  }
  dnsDialogVisible.value = true
}

// 保存PubYun密钥
const savePubyunKey = async () => {
  try {
    await keyFormRef.value.validate()
    keyLoading.value = true
    
    const params = new URLSearchParams()
    params.append('keyID', keyForm.value.keyID)
    params.append('keySecret', keyForm.value.keySecret)
    
    const response = await fetch(`${API_BASE_URL}/update-pubyunKey`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '保存失败')
    }
    
    ElMessage.success('PubYun配置保存成功')
    keyDialogVisible.value = false
    fetchData()
  } catch (error) {
    console.error('保存PubYun配置失败:', error)
    ElMessage.error(`保存失败: ${error.message || '未知错误'}`)
  } finally {
    keyLoading.value = false
  }
}

// 保存DNS记录
const saveDnsRecord = async () => {
  try {
    await recordFormRef.value.validate()
    recordLoading.value = true
    
    const params = new URLSearchParams()
    params.append('name', currentRecord.value.name)
    params.append('domainName', currentRecord.value.domainName)
    params.append('type', currentRecord.value.type)
    params.append('flagStr', 'auto') // PubYun只支持自动获取
    
    const endpoint = (dnsDialogMode.value === 'edit') ? '/update-pubyunDns' : '/add-pubyunDns'
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '操作失败')
    }
    
    ElMessage.success('DNS记录保存成功')
    dnsDialogVisible.value = false
    fetchData()
  } catch (error) {
    console.error('保存DNS记录失败:', error)
    ElMessage.error(`保存失败: ${error.message || '未知错误'}`)
  } finally {
    recordLoading.value = false
  }
}

// 更新DNS记录
const updateDns = async (record) => {
  try {
    const params = new URLSearchParams()
    params.append('name', record.name)
    params.append('domainName', record.domainName)
    params.append('type', record.type)
    params.append('flagStr', 'auto') // PubYun只支持自动获取
    
    const response = await fetch(`${API_BASE_URL}/update-pubyunDns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '更新失败')
    }
    
    ElMessage.success('DNS记录更新成功')
    fetchData()
  } catch (error) {
    console.error('更新DNS记录失败:', error)
    ElMessage.error(`更新失败: ${error.message || '未知错误'}`)
  }
}

// 删除DNS记录
const deleteDns = async (record) => {
  try {
    await ElMessageBox.confirm('确定要删除这条DNS记录吗?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const params = new URLSearchParams()
    params.append('name', record.name)
    
    const response = await fetch(`${API_BASE_URL}/delete-pubyunDns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '删除失败')
    }
    
    ElMessage.success('DNS记录删除成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除DNS记录失败:', error)
      ElMessage.error(`删除失败: ${error.message || '未知错误'}`)
    }
  }
}

// 显示日志对话框
const showLogDialog = () => {
  logDialogVisible.value = true
  fetchLogs()
  // 开启日志自动刷新（如果开启状态）
  if (autoRefreshEnabled.value) {
    startLogAutoRefresh()
  }
}

// 切换自动刷新
const toggleAutoRefresh = (enabled) => {
  if (enabled) {
    startLogAutoRefresh()
    ElMessage.success('已开启自动刷新（5秒/次）')
  } else {
    stopLogAutoRefresh()
    ElMessage.info('已关闭自动刷新')
  }
}

// 启动日志自动刷新
const startLogAutoRefresh = () => {
  stopLogAutoRefresh() // 先停止已有的定时器
  logRefreshInterval.value = setInterval(() => {
    if (logDialogVisible.value) {
      fetchLogs(false) // 静默刷新，不显示加载状态
    }
  }, 5000)
}

// 停止日志自动刷新
const stopLogAutoRefresh = () => {
  if (logRefreshInterval.value) {
    clearInterval(logRefreshInterval.value)
    logRefreshInterval.value = null
  }
}

// 获取日志
const fetchLogs = async (showLoading = true) => {
  try {
    if (showLoading) {
      logLoading.value = true
    }
    
    const params = new URLSearchParams({
      module: 'dns',
      page: logCurrentPage.value,
      size: logPageSize.value
    })
    
    const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '获取日志失败')
    }
    
    logsData.value = data.data || []
    logTotal.value = data.count || 0
    
    // 只有在非自动刷新（即手动操作）时才滚动到顶部
    if (showLoading && logContentRef.value) {
      logContentRef.value.scrollTop = 0
    }
  } catch (error) {
    console.error('获取日志失败:', error)
    if (showLoading) {
      ElMessage.error(`获取日志失败: ${error.message || '未知错误'}`)
    }
  } finally {
    if (showLoading) {
      logLoading.value = false
    }
  }
}

// 刷新日志
const refreshLogs = () => {
  fetchLogs(true)
}

// 清空日志
const clearLogs = async () => {
  try {
    await ElMessageBox.confirm('确定要清空所有PubYun DDNS日志吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    logClearing.value = true
    
    const params = new URLSearchParams({
      module: 'dns'
    })
    
    const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '清空日志失败')
    }
    
    ElMessage.success('日志已清空')
    fetchLogs(true)
  } catch (error) {
    if (error !== 'cancel') {
      console.error('清空日志失败:', error)
      ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`)
    }
  } finally {
    logClearing.value = false
  }
}

// 下载所有日志
const downloadAllLogs = async () => {
  try {
    logDownloading.value = true
    
    // 获取所有日志（设置一个足够大的size值）
    const params = new URLSearchParams({
      module: 'dns',
      page: 1,
      size: 10000 // 设置一个较大的数值，获取尽可能多的日志
    })
    
    const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '获取日志失败')
    }
    
    const allLogs = data.data || []
    
    if (allLogs.length === 0) {
      ElMessage.warning('暂无日志可下载')
      return
    }
    
    // 格式化所有日志内容
    let logContent = ''
    allLogs.forEach(log => {
      logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
    })
    
    // 创建下载链接
    const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `pubyun_ddns_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`)
  } catch (error) {
    console.error('下载日志失败:', error)
    ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`)
  } finally {
    logDownloading.value = false
  }
}

// 处理每页条数变化
const handleLogSizeChange = (size) => {
  logPageSize.value = size
  logCurrentPage.value = 1
  fetchLogs(true)
}

// 处理页码变化
const handleLogPageChange = (page) => {
  logCurrentPage.value = page
  fetchLogs(true)
}

// 获取数据
const fetchData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/pubyunDns`, {
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '获取数据失败')
    }
    
    // 更新用户信息
    isAdmin.value = data.isAdmin
    isLogin.value = data.isLogin
    loginUsername.value = data.loginUsername
    
    // 更新密钥信息
    if (data.data?.keyID) {
      keyForm.value.keyID = data.data.keyID
      keyForm.value.keySecret = data.data.keySecret
    }
    
    // 更新DDNS列表
    if (data.data?.pubyunMap) {
      dnsList.value = data.data.pubyunMap || []
    }
    
    // 更新开关状态
    dnsSwitch.value = data.dnsSwitch
  } catch (error) {
    console.error('获取数据失败:', error)
    ElMessage.error(`获取数据失败: ${error.message || '未知错误'}`)
  }
}

// 窗口大小变化处理
const handleResize = () => {
  checkMobile()
}

// 初始化
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', handleResize)
  fetchData()
  // 设置自动刷新，每5秒刷新一次数据
  refreshInterval.value = setInterval(fetchData, 5000)
})

// 组件卸载时清除定时器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }
  stopLogAutoRefresh()
})
</script>

<style scoped lang="scss">
.pubyun-ddns-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.config-card,
.ddns-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.12);
  }
  
  .card-header {
    padding: 16px 24px;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f9fafc;
    
    .card-title-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      
      .card-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        margin: 0;
      }
    }
    
    .card-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .card-body {
    padding: 24px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      
      .card-title-wrapper {
        width: 100%;
      }
      
      .card-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
    
    .card-body {
      padding: 16px;
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
      
      .card-title-wrapper {
        .card-title {
          font-size: 15px;
        }
      }
    }
    
    .card-body {
      padding: 12px;
    }
  }
}

.alert-message {
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    font-size: 12px;
    
    :deep(.el-alert__title) {
      font-size: 12px;
    }
  }
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  .info-item {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px dashed #ebeef5;
    
    .info-label {
      min-width: 80px;
      font-weight: 600;
      color: #606266;
    }
    
    .info-value {
      flex: 1;
      color: #303133;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    
    .info-item {
      flex-direction: column;
      gap: 4px;
      padding: 6px 0;
      
      .info-label {
        min-width: auto;
        font-size: 13px;
      }
      
      .info-value {
        font-size: 14px;
      }
    }
  }
  
  @media (max-width: 480px) {
    .info-item {
      .info-label {
        font-size: 12px;
      }
      
      .info-value {
        font-size: 13px;
      }
    }
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.ddns-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 8px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
        font-size: 13px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &.action-cell {
        padding: 10px 12px;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    td {
      padding: 8px 10px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
    }
  }
}

.status-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.enabled {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.disabled {
    background-color: #f5f5f5;
    color: #909399;
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.success {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.danger {
    background-color: #fef0f0;
    color: #f56c6c;
  }
  
  &.warning {
    background-color: #fdf6ec;
    color: #e6a23c;
  }
  
  &.info {
    background-color: #f5f5f5;
    color: #909399;
  }
}

.action-cell {
  .action-buttons {
    display: flex;
    gap: 4px;
    flex-wrap: nowrap;
    
    .el-button {
      flex-shrink: 0;
    }
    
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      justify-content: flex-end;
    }
    
    .el-button--small {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .action-buttons {
      gap: 2px;
      
      .el-button--small {
        padding: 4px 6px;
        font-size: 11px;
      }
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
}

.readonly-input {
  background-color: #f5f7fa;
  color: #909399;
  
  :deep(.el-input__inner) {
    background-color: #f5f7fa;
    color: #909399;
    cursor: not-allowed;
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog {
  :deep(.el-dialog) {
    @media (max-width: 768px) {
      width: 95% !important;
      margin-top: 5vh !important;
    }
    
    @media (max-width: 480px) {
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      max-height: 100%;
      border-radius: 0;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      margin-bottom: 0 !important;
      
      .el-dialog__header {
        padding: 12px 16px;
        border-bottom: 1px solid #ebeef5;
      }
      
      .el-dialog__body {
        padding: 16px;
        max-height: calc(80vh - 120px);
        overflow-y: auto;
      }
      
      .el-dialog__footer {
        padding: 12px 16px;
        border-top: 1px solid #ebeef5;
      }
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-form-item__label) {
  @media (max-width: 768px) {
    font-size: 13px;
    padding-bottom: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>